import React from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Portal,
} from '@mui/material';
import { useMutation, useLazyQuery, gql } from '@apollo/client';
import mixpanel from 'mixpanel-browser';
import { capitalize } from 'lodash';
import CustomContainer from '../CustomContainer';
import { UPDATE_PET_PARENT_ANIMAL, GET_ANIMAL_MEDIA_DOWNLOAD_URLS } from '../../../queries';
import { useHubPetParent } from '../HubPetParentContext';
import { PetParentAnimal } from '../../../types/PetParent';
import { getAnimalEmoji, a11yProps } from '../utils';
import PetProfileView from './PetProfileView';
import PetProfileEdit from './PetProfileEdit';
import PetRecordsSection from './PetRecordsSection';
import PetResourcesSection from './PetResourcesSection';
import ChangesSavedDialog from './ChangesSavedDialog';
import useHubPageView from '../useHubPageView';

function MyPets() {
  useHubPageView();
  const [activeTab, setActiveTab] = React.useState(0);
  const [isEditing, setIsEditing] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');

  const copyText = async (text: string, message: string) => {
    await navigator.clipboard.writeText(text);
    setSnackbarMessage(message);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (isEditing && hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      setActiveTab(newValue);
      setIsEditing(false);
    }
  };

  const { petParent, loading } = useHubPetParent();
  if (petParent?.id === '230464') {
    throw new Error('Test error');
  }

  const [updatePetParentAnimal, { client }] = useMutation(UPDATE_PET_PARENT_ANIMAL);
  const [fetchMediaUrls, { data: mediaUrlsData }] = useLazyQuery(GET_ANIMAL_MEDIA_DOWNLOAD_URLS);

  const pets = React.useMemo(
    () => petParent?.animals?.filter((a: PetParentAnimal) => ['Adopted', 'Co-Sheltering'].includes(a.careType)) ?? [],
    [petParent],
  );
  const currentPetId = pets[activeTab]?.id;

  React.useEffect(() => {
    if (currentPetId) {
      fetchMediaUrls({ variables: { animalId: parseInt(String(currentPetId), 10) } });
    }
  }, [currentPetId, fetchMediaUrls]);

  // Create clean petParent object with only necessary fields (matching old defaultValues pattern)
  const cleanPetParent = React.useMemo(() => {
    if (!petParent) return null;
    const { awo } = petParent;
    return {
      id: petParent.id,
      firstName: capitalize(petParent.firstName || ''),
      lastName: capitalize(petParent.lastName || ''),
      email: petParent.email?.toLowerCase() || '',
      phoneNumber: petParent.phoneNumber,
      streetAddress: petParent.streetAddress,
      state: petParent.state,
      country: petParent.country,
      awoName: awo.name,
    };
  }, [petParent]);

  if (!petParent || loading) return null;

  const { awo } = petParent;

  if (!loading && pets.length === 0) {
    return null;
  }

  const currentPet = pets[activeTab];
  const urls = mediaUrlsData?.animalMediaDownloadUrls;
  const medicalRecordUrl = urls?.pdfUrl || urls?.awsUrl || null;

  const adoptionPacketLink = currentPet?.animalType === 'Dog'
    ? awo.adoptionPacketLinkDogs
    : awo.adoptionPacketLinkCats;

  const handleEdit = () => {
    mixpanel.track('pet_profile_edit_click', { pet_name: currentPet.name });
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleCancelChanges = () => {
    setShowUnsavedDialog(false);
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const handleKeepEditing = () => {
    setShowUnsavedDialog(false);
  };

  const handleSave = async (updatedData: Partial<PetParentAnimal>) => {
    const newData = {
      ...cleanPetParent,
      animals: [{
        ...currentPet,
        ...updatedData,
        awo: {
          name: awo.name,
        },
      }],
    };

    const oldData = cleanPetParent;

    try {
      await updatePetParentAnimal({
        variables: {
          oldData,
          newData,
        },
      });

      client.writeFragment({
        id: `Animal:${currentPet.id}`,
        fragment: gql`
          fragment UpdatedAnimal on Animal {
            name
            birthDate
            primaryBreed
            gender
            exitDate
          }
        `,
        data: updatedData,
      });

      mixpanel.track('pet_profile_save_click', { pet_name: currentPet.name });
      setIsEditing(false);
      setHasUnsavedChanges(false);
      setSnackbarMessage('Changes saved');

      setTimeout(() => {
        setSnackbarMessage('');
        setShowSuccessDialog(false);
      }, 2500);
    } catch (error) {
      setSnackbarMessage('Failed to save changes');
    }
    // ToDo: Remove after BE implementation
    // setShowSuccessDialog(true);
  };

  const handleCopyMicrochip = () => {
    mixpanel.track('microchip_number_copied', undefined, () => {
      copyText(currentPet.microchipNumber || '', 'Microchip copied');
    });
  };

  return (
    <>
      <CustomContainer wide sx={{ pt: 3, pb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="sectionTitle">My Pets</Typography>
            <Typography variant="body2">All their important information in one place</Typography>
          </Box>
        </Box>
      </CustomContainer>

      {pets.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <CustomContainer sx={{ mx: -2 }} wide>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="pet tabs"
            >
              {pets.map((pet: PetParentAnimal, index: number) => (
                <Tab
                  key={pet.id}
                  label={`${getAnimalEmoji(pet.animalType)} ${pet.name}`}
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </CustomContainer>
        </Box>
      )}

      <Box sx={{ background: '#F6F5FA', pb: 4, flexGrow: 1 }}>
        <CustomContainer sx={{ pt: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            }}
          >
            <Box>
              {isEditing ? (
                <PetProfileEdit
                  animal={currentPet}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onFormChange={() => setHasUnsavedChanges(true)}
                />
              ) : (
                <PetProfileView animal={currentPet} onEdit={handleEdit} />
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <PetRecordsSection
                animal={currentPet}
                onCopyMicrochip={handleCopyMicrochip}
                medicalRecordUrl={medicalRecordUrl}
              />
              <PetResourcesSection
                awoName={awo?.displayName ?? awo?.name}
                faqLink={awo.faqLink}
                adoptionPacketLink={adoptionPacketLink}
              />
            </Box>
          </Box>
        </CustomContainer>
      </Box>

      <Dialog
        open={showUnsavedDialog}
        onClose={handleKeepEditing}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '18px',
            maxWidth: 361,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 1.5,
            px: 5.5,
            pt: 5,
          }}
        >
          Unsaved changes
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', px: 5.5, pb: 2.5 }}>
          <Typography>You have unsaved changes. If you leave now, your changes will be lost.</Typography>
        </DialogContent>
        <Box sx={{
          px: 5.5,
          pb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
        >
          <Button
            onClick={handleCancelChanges}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: '#D32F2F',
              '&:hover': { backgroundColor: '#B71C1C' },
              borderRadius: 2,
              py: 1,
            }}
          >
            Discard changes
          </Button>
          <Button
            onClick={handleKeepEditing}
            fullWidth
            sx={{
              color: '#000',
              py: 1,
            }}
          >
            Keep editing
          </Button>
        </Box>
      </Dialog>

      <ChangesSavedDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      />

      <Portal container={() => document.getElementById('root')}>
        <Snackbar
          open={Boolean(snackbarMessage)}
          autoHideDuration={2000}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          message={snackbarMessage}
          onClose={() => setSnackbarMessage('')}
          onClick={() => setSnackbarMessage('')}
        />
      </Portal>
    </>
  );
}

export default MyPets;
